/**
 * The airframe: the air going past it, the air coming off it, and the horn that warns you.
 *
 * ```
 *   white ─► lowpass(f ← IAS) ─► highpass(140) ─► wind gain(← IAS²·⁴) ─────────┐
 *                                                                              │
 *   white ─► bandpass(165, Q1.2) ─► [AM by 14 Hz shudder] ─► buffet gain ──────┼─► out
 *                                                                              │
 *   square + square(detuned) ─► bandpass(3050, Q6) ─► horn gain ───────────────┘
 * ```
 *
 * ## Wind
 *
 * Filtered noise whose brightness and level both track indicated airspeed, because the sound of
 * speed is a *spectrum* change and not merely a volume change — a quiet recording of 140 kt still
 * sounds fast, and a loud recording of 50 kt still sounds slow. The highpass at 140 Hz is what
 * stops it turning into a rumble that competes with the engine for the same octave.
 *
 * ## Buffet, and why it comes first
 *
 * As a wing approaches its stall the flow separates and the wake batters the tail. In the
 * aeroplane that arrives through the seat and the stick before it arrives through your ears, and
 * pilots fly by it: the burble is the warning, and the horn is confirmation. So the buffet layer
 * starts at 0.68 of stall alpha and the horn at 0.80, and `model.test.ts` asserts that ordering
 * so that a later tuning pass cannot silently invert it.
 *
 * It is a 165 Hz noise band amplitude-modulated at 14 Hz — low enough to be felt as a rhythm
 * rather than heard as a pitch, which is what separates "buffet" from "a buzzing noise".
 *
 * ## Horn
 *
 * A C172's stall warner is a pneumatic reed: suction through a slot in the wing leading edge
 * blows a whistle. It is not a clean tone, so this is two square waves a few cents apart through
 * a narrow bandpass — the detuning gives it the beating roughness of a reed, and the bandpass
 * takes the worst of the square's upper harmonics off without making it polite. It is supposed to
 * be annoying.
 *
 * `updateFrame(alpha)`: {@link AirframeVoice.updateFrame}, once per rendered frame.
 * Allocation-free.
 */

import type { AudioBank } from '../bank.js';
import {
  BUFFET_CENTRE_HZ,
  BUFFET_SHUDDER_HZ,
  buffetGain,
  stallHornActive,
  stallProximity,
  windCutoffHz,
  windGain,
} from '../model.js';
import { setTarget } from '../param.js';
import type { AircraftSoundConfig } from '../soundSet.js';
import type { AircraftAudioState } from '../state.js';
import { bandpass, disconnectAll, gainNode, highpass, loopingSource, lowpass } from './parts.js';

export class AirframeVoice {
  private readonly context: AudioContext;
  private readonly config: AircraftSoundConfig;

  private readonly windSource: AudioBufferSourceNode;
  private readonly windLow: BiquadFilterNode;
  private readonly windHigh: BiquadFilterNode;
  private readonly windLevel: GainNode;

  private readonly buffetSource: AudioBufferSourceNode;
  private readonly buffetBand: BiquadFilterNode;
  private readonly buffetAm: GainNode;
  private readonly buffetShudder: OscillatorNode;
  private readonly buffetShudderDepth: GainNode;
  private readonly buffetLevel: GainNode;

  private readonly hornA: OscillatorNode;
  private readonly hornB: OscillatorNode;
  private readonly hornBand: BiquadFilterNode;
  private readonly hornLevel: GainNode;

  private readonly output: GainNode;

  /** Previous answer from the horn's Schmitt trigger. */
  private hornWasOn = false;

  constructor(
    context: AudioContext,
    bank: AudioBank,
    destination: AudioNode,
    config: AircraftSoundConfig,
  ) {
    this.context = context;
    this.config = config;

    this.output = gainNode(context, config.airframeTrim);
    this.output.connect(destination);

    // --- wind -----------------------------------------------------------------------------
    this.windLevel = gainNode(context, 0);
    this.windLevel.connect(this.output);
    this.windHigh = highpass(context, 140);
    this.windHigh.connect(this.windLevel);
    this.windLow = lowpass(context, 400, 0.8);
    this.windLow.connect(this.windHigh);
    this.windSource = loopingSource(context, bank.white, bank.whiteLoopEndS, 1, 0.07);
    this.windSource.connect(this.windLow);

    // --- buffet ---------------------------------------------------------------------------
    this.buffetLevel = gainNode(context, 0);
    this.buffetLevel.connect(this.output);
    this.buffetAm = gainNode(context, 0.45);
    this.buffetAm.connect(this.buffetLevel);
    this.buffetBand = bandpass(context, BUFFET_CENTRE_HZ, 1.2);
    this.buffetBand.connect(this.buffetAm);
    // A third read head into the white buffer, again at its own offset and rate: the buffet must
    // not be a filtered copy of the wind or the two will phase against each other.
    this.buffetSource = loopingSource(context, bank.white, bank.whiteLoopEndS, 0.89, 2.9);
    this.buffetSource.connect(this.buffetBand);

    this.buffetShudder = context.createOscillator();
    this.buffetShudder.type = 'sine';
    this.buffetShudder.frequency.value = BUFFET_SHUDDER_HZ;
    this.buffetShudderDepth = gainNode(context, 0.45);
    this.buffetShudder.connect(this.buffetShudderDepth);
    this.buffetShudderDepth.connect(this.buffetAm.gain);
    this.buffetShudder.start(0);

    // --- stall horn ------------------------------------------------------------------------
    this.hornLevel = gainNode(context, 0);
    this.hornLevel.connect(this.output);
    this.hornBand = bandpass(context, config.hornHz, 6);
    this.hornBand.connect(this.hornLevel);
    this.hornA = context.createOscillator();
    this.hornA.type = 'square';
    this.hornA.frequency.value = config.hornHz;
    this.hornB = context.createOscillator();
    this.hornB.type = 'square';
    // Eleven hertz apart: enough beating to sound like a reed under pressure, not so much that
    // it reads as two separate horns.
    this.hornB.frequency.value = config.hornHz + 11;
    const hornMix = gainNode(context, 0.5);
    this.hornA.connect(hornMix);
    this.hornB.connect(hornMix);
    hornMix.connect(this.hornBand);
    this.hornA.start(0);
    this.hornB.start(0);
  }

  /** True while the stall horn is sounding — read by the tests and the debug overlay. */
  get hornSounding(): boolean {
    return this.hornWasOn;
  }

  updateFrame(state: AircraftAudioState, dopplerRatio: number): void {
    const now = this.context.currentTime;
    const ias = Math.max(0, state.indicatedAirspeedMps);

    // --- wind -------------------------------------------------------------------------------
    setTarget(this.windLow.frequency, windCutoffHz(ias) * dopplerRatio, 0.08, now);
    setTarget(this.windLevel.gain, windGain(ias) * 0.5, 0.08, now);

    // --- buffet -----------------------------------------------------------------------------
    const proximity = stallProximity(state.alphaRad, this.config.stallAlphaRad);
    /*
     * Gated on airspeed for the same reason the flight model gates its own stall flag: an
     * aeroplane parked in a tailwind is at a large angle of attack and is not about to stall.
     * Without this the buffet runs continuously on the apron.
     */
    const flying = ias > 12 ? 1 : 0;
    setTarget(this.buffetLevel.gain, buffetGain(proximity) * flying * 0.7, 0.06, now);
    setTarget(this.buffetBand.frequency, BUFFET_CENTRE_HZ * dopplerRatio, 0.1, now);

    // --- horn -------------------------------------------------------------------------------
    const hornOn = this.config.stallHorn && flying === 1 && stallHornActive(proximity, this.hornWasOn);
    this.hornWasOn = hornOn;
    // Fast, but not instant: a real reed takes a few tens of milliseconds to speak, and a horn
    // that switches on in one sample is a click with a tone after it.
    setTarget(this.hornLevel.gain, hornOn ? 0.09 : 0, 0.025, now);
    setTarget(this.hornBand.frequency, this.config.hornHz * dopplerRatio, 0.05, now);
    setTarget(this.hornA.frequency, this.config.hornHz * dopplerRatio, 0.05, now);
    setTarget(this.hornB.frequency, (this.config.hornHz + 11) * dopplerRatio, 0.05, now);
  }

  dispose(): void {
    this.buffetShudder.stop();
    this.hornA.stop();
    this.hornB.stop();
    this.windSource.stop();
    this.buffetSource.stop();
    disconnectAll([
      this.windSource,
      this.windLow,
      this.windHigh,
      this.windLevel,
      this.buffetSource,
      this.buffetBand,
      this.buffetAm,
      this.buffetShudder,
      this.buffetShudderDepth,
      this.buffetLevel,
      this.hornA,
      this.hornB,
      this.hornBand,
      this.hornLevel,
      this.output,
    ]);
  }
}
